import { html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { fs24Edit } from '@cof/fs-icons';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './communication-v2.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';
import '../../../edit-card/edit-card.component.js';
import '../../step-identity/ssn/ssn.component.js';
import '../../step-identity/dob/dob.component.js';
import { validateForm } from '../../../../shared/decorators/validate-form.decorator.js';
import { SessionService } from '../../../../core/services/session/session.service.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { IconicV2BaseComponent } from '../../../iconic-v2-base/iconic-v2-base.component.js';

class RefiPersonalCommunicationComponent extends IconicV2BaseComponent {
  constructor() {
    super();
    this.primaryAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.PRIMARY_APPLICANT;
    this.coAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.CO_APPLICANT;

    this.validity = {};
    this.pending = false;
    this.displayError = false;
    this.applicantType = this.primaryAppType;
    this.coAppPrefix = '';
    this.isPrimary = true;
    this.hidePrimarySSN = false;

    this.confirmationPage = false;
    this.mockApplication = false;
    this.inactive = false;

    this.compact = false;
    this.cardFired = false;

    this.childIds = {};

    this.isCommunicationStepV2 = false;

    FsIcon.add(fs24Edit);
  }

  static properties = {
    applicantType: { type: String, attribute: 'applicant-type' },
    applicant: { type: Object, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    displayError: { type: Boolean, attribute: 'display-error', reflect: true },
    compact: { type: Boolean, reflect: true },
    confirmationPage: {
      type: Boolean,
      reflect: true,
      attribute: 'confirmation-page',
    },
    mockApplication: {
      type: Boolean,
      reflect: true,
      attribute: 'mock-application',
    },
    inactive: { type: Boolean, attribute: 'inactive', reflect: true },
    pending: { type: Boolean, attribute: 'pending', reflect: true },
    isJointApplication: { type: Boolean, attribute: 'is-joint-application' },
    isCommunicationStepV2: {
      type: Boolean,
      attribute: 'is-communication-step-v2',
    },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();

    this.isPrimary = !(this.applicantType === this.coAppType);
    this.coAppPrefix = this.applicantType === this.coAppType ? 'co-' : '';

    this.hidePrimarySSN = ApplicationService.hidePrimarySSN;

    this.childIds = {
      name: `${this.coAppPrefix}name-input`,
      email: `${this.coAppPrefix}email-input`,
      phone: `${this.coAppPrefix}phone-input`,
      dob: `${this.coAppPrefix}dateOfBirthInput`,
      ssn: `${this.coAppPrefix}ssnInput`,
    };

    this.cardFormId = `${this.coAppPrefix}-communication-cardForm`;
  }

  firstUpdated() {
    super.firstUpdated();
    this.inputChildren = {
      name: this.shadowRoot.getElementById(this.childIds.name),
      email: this.shadowRoot.getElementById(this.childIds.email),
      phone: this.shadowRoot.getElementById(this.childIds.phone),
      dob: this.shadowRoot.getElementById(this.childIds.dob),
      ssn: this.shadowRoot.getElementById(this.childIds.ssn),
    };
  }

  publishViewTag() {
    const tagKey = this.isPrimary
      ? TagKey.VIEW_PERSONAL_REVIEW
      : TagKey.VIEW_COBORROWER_PERSONAL_REVIEW;
    stratum.publishTag(tagKey);
  }

  dobDisplayText() {
    if (ApplicationService.Application[this.applicantType].dateOfBirth) {
      let dob = ApplicationService.Application[this.applicantType].dateOfBirth;
      dob = `${dob.substring(0, 3)}**${dob.substring(5)}`;
      return dob;
    }
    return '';
  }

  ssnDisplayText() {
    const ssnStatus = {
      primaryApplicant: !ApplicationService.getPrimaryApplicantSsnChanged,
      coApplicant: !ApplicationService.getCoApplicantSsnChanged,
    };

    const applicant = ApplicationService.Application[this.applicantType];
    const hasUnchangedSsn = ssnStatus[this.applicantType];

    // Display last four from tax ID if available and SSN hasn't been changed
    if (applicant.lastFourTaxId && hasUnchangedSsn && !applicant.ssn) {
      return `***-**-${applicant.lastFourTaxId}`;
    }

    // Display last four from SSN if available
    if (applicant.ssn) {
      return `***-**-${applicant.ssn.slice(-4)}`;
    }

    return '';
  }

  showEditError() {
    this.debug.log('Display editing error');
    this.displayError = true;
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderFields() {
    return html` <div class="communication-fields">
      <refi-personal-name
        ?compact="${this.compact}"
        id="${this.childIds.name}"
        @validity="${this.updateValidity}"
        applicant-type=${this.isPrimary ? this.primaryAppType : this.coAppType}
      ></refi-personal-name>

      <refi-personal-email
        id="${this.childIds.email}"
        @validity="${this.updateValidity}"
        .isPrimary="${this.isPrimary}"
        ?is-prefill="${this.isPrefill}"
      ></refi-personal-email>

      <refi-personal-phone
        id="${this.childIds.phone}"
        @validity="${this.updateValidity}"
        .isPrimary="${this.isPrimary}"
      ></refi-personal-phone>

      <div
        class="${(!this.isPrimary && this.compact) ||
        (this.isPrimary && !(this.isPrefill && this.hidePrimarySSN))
          ? 'compact-dob-ssn'
          : ''}"
      >
        <refi-personal-dob
          id="${this.childIds.dob}"
          @validity="${this.updateValidity}"
          .isPrimary="${this.isPrimary}"
        ></refi-personal-dob>

        <refi-personal-ssn
          class="${this.isPrimary && this.isPrefill && this.hidePrimarySSN
            ? 'hidden'
            : ''}"
          id="${this.childIds.ssn}"
          @validity="${this.updateValidity}"
          @pending="${this.updatePending}"
          .isPrimary="${this.isPrimary}"
          ?is-communication-step-v2="${this.isCommunicationStepV2}"
        ></refi-personal-ssn>
      </div>
    </div>`;
  }

  renderCardForm() {
    if (!this.cardFired) {
      this.cardFired = true;
      this.publishViewTag();
    }
    return html` <refi-personal-edit-card
      card-title="Personal"
      component-id="${this.coAppPrefix}personal-edit-card"
      id="${this.coAppPrefix}personal-edit-card"
      card-icon="fs-24-user"
      .displayText="${this.getDisplayText()}"
      ?communication-classes-present=${true}
      ?truncate-display=${true}
      ?inactive="${this.inactive}"
      ?show-error="${this.displayError}"
      ?loading="${this.pending}"
      @edit-clicked="${this.editClicked}"
      @save-clicked="${this.saveClicked}"
      @cancel-clicked="${this.cancelClicked}"
    >
      <form
        autocomplete="off"
        novalidate
        @submit="${this.onSubmit}"
        id="${this.cardFormId}"
      >
        ${this.renderFields()}
      </form>
    </refi-personal-edit-card>`;
  }

  getDisplayText() {
    let displayText = [
      {
        type: 'fullName',
        content: `${ApplicationService.Application[this.applicantType].firstName} ${ApplicationService.Application[this.applicantType].lastName}`,
      },
      {
        type: 'email',
        content:
          ApplicationService.Application[this.applicantType].emailAddress,
      },
      {
        type: 'phone',
        content: ApplicationService.Application[this.applicantType].phoneNumber,
      },
      {
        type: 'dob',
        content: `${this.dobDisplayText()}`,
      },
    ];

    if (!this.isPrimary || !(this.hidePrimarySSN && this.isPrefill)) {
      displayText.push({
        type: 'lastFourSsn',
        content: `${this.ssnDisplayText()}`,
      });
    }

    return displayText;
  }

  renderSsnMismatchModal() {
    return html` <fs-modal-dialog
      accessible-title="ssnMismatchModal"
      id="ssnMismatchModal"
      dismissible
      size="small"
    >
      <refi-heading slot="modal-dialog-header" id="mismatchModalHeader">
        Hmm, that Social Security number doesn't match what we verified with
        your access code.
      </refi-heading>
      <section
        class="fs-body-text fs-body-text--normal"
        slot="modal-dialog-body"
      >
        <fs-body-text size="normal" variant="primary">
          In order to receive your personalized offers, the last four digits of
          the Social Security number you provided needs to match the information
          entered with your personal details.
        </fs-body-text>
      </section>
      <fs-modal-dialog-footer slot="modal-dialog-footer">
        <fs-button
          variant="primary"
          type="button"
          @click="${this.removeOfferAndContinue}"
        >
          Continue without offer
        </fs-button>
        <fs-button
          variant="secondary"
          type="button"
          outlined=""
          @click="${this.closeSsnMismatchModal}"
        >
          Edit your details
        </fs-button>
      </fs-modal-dialog-footer>
    </fs-modal-dialog>`;
  }

  render() {
    if (this.confirmationPage && !this.isPrimary) {
      return html`${this.renderCardForm()} ${this.renderSsnMismatchModal()}`;
    }
    if ((this.isPrimary && this.isPrefill) || this.confirmationPage) {
      return html`${this.renderCardForm()} ${this.renderSsnMismatchModal()}`;
    }
    return html`${this.renderFields()} ${this.renderSsnMismatchModal()}`;
  }

  removeOfferAndContinue() {
    this.debug.log('removing access code offer');
    SessionService.removeCachedOffer();

    ApplicationService.Application = {
      ...ApplicationService.Application,
      promoCode: null,
    };

    stratum.publishTag(
      TagKey.PERSONAL_IDENTITY_SSN_NO_MATCH_CONTINUE_WITHOUT_OFFER
    );

    const cardComponent = this.shadowRoot.getElementById(
      `${this.coAppPrefix}personal-edit-card`
    );

    this.debug.log('valid', ApplicationService.Application);
    cardComponent?.closeEditing();
    this.displayError = false;
    this.dispatchEvent(
      new CustomEvent('edit-card-closed', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-personal-communication`,
        },
      })
    );

    this.closeSsnMismatchModal();
    this.saveInfoToApplicant();
  }

  openSsnMismatchModal() {
    stratum.publishTag(TagKey.PERSONAL_IDENTITY_ACCESS_CODE_SSN_NO_MATCH);
    const modal = this.shadowRoot.getElementById('ssnMismatchModal');

    const invalidSsnValidityEvent = new CustomEvent('invalid-ssn', {
      detail: { name: 'ssn', isValid: false },
    });
    this.updateValidity(invalidSsnValidityEvent);

    modal?.openModalDialog();
  }

  closeSsnMismatchModal() {
    stratum.publishTag(TagKey.PERSONAL_IDENTITY_SSN_NO_MATCH_EDIT_DETAILS);

    const validSsnValidityEvent = new CustomEvent('invalid-ssn', {
      detail: { name: 'ssn', isValid: true },
    });
    this.updateValidity(validSsnValidityEvent);

    const modal = this.shadowRoot.getElementById('ssnMismatchModal');
    modal?.closeModalDialog();
  }

  updateValidity(ev) {
    this.validity[`${this.coAppPrefix}${ev.detail.name}`] = ev.detail.isValid;
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: `${this.coAppPrefix}${ev.detail.name}`,
          isValid: ev.detail.isValid,
        },
      })
    );
  }

  updatePending(ev) {
    this.pending = ev.detail;
    this.dispatchEvent(new CustomEvent('pending', { detail: this.pending }));
  }

  cancelClicked() {
    stratum.publishTag(TagKey.PQ_CONFIRMATION_CANCEL_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === this.coAppType ? 'co borrower ' : ''
        }personal`,
      },
    });
    // Reset fields to original values (from Application)
    this.dispatchEvent(
      new CustomEvent('edit-card-closed', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-personal-communication`,
        },
      })
    );
    this.displayError = false;

    this.resetValues();
  }

  resetValues() {
    Object.values(this.inputChildren).forEach((child) => {
      this.debug.log('Updating', child);
      child?.resetValues();
    });
    this.requestUpdate();
  }

  editClicked() {
    if (this.applicantType === this.coAppType) {
      stratum.publishTag(TagKey.VIEW_COBORROWER_PERSONAL_EDIT);
    } else {
      stratum.publishTag(TagKey.VIEW_PERSONAL_EDIT);
    }
    stratum.publishTag(TagKey.PQ_CONFIRMATION_EDIT_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === this.coAppType ? 'co borrower ' : ''
        }personal`,
      },
    });
    // Send event to step that the card is open
    this.dispatchEvent(
      new CustomEvent('edit-card-open', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-personal-communication`,
        },
      })
    );
  }

  saveClicked() {
    stratum.publishTag(TagKey.PQ_CONFIRMATION_SAVE_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === this.coAppType ? 'co borrower ' : ''
        }personal`,
      },
    });
    // Trigger form submission in card
    const form = this.shadowRoot.getElementById(this.cardFormId);
    form.requestSubmit();
  }

  checkSsnMismatch() {
    // offer code ssn matches applicant ssn check
    const manualLastFour = this.inputChildren.ssn.values.ssn.substring(7);
    const prefilledLastFour =
      ApplicationService.Application[this.applicantType].lastFourTaxId;
    const applicantLastFour =
      manualLastFour.length === 4 ? manualLastFour : prefilledLastFour;

    const offerLastFour = SessionService.getCachedOffer()?.lastFourTaxId;
    if (offerLastFour) {
      this.debug.log('Checking offer last four against', applicantLastFour);
    }

    if (
      offerLastFour &&
      this.isPrimary &&
      applicantLastFour !== offerLastFour
    ) {
      trackAnalyticsEvent('access code ssn no match');
      this.debug.log('Access code SSN no match');
      this.openSsnMismatchModal();
      stratum.publishTag(TagKey.PERSONAL_IDENTITY_ACCESS_CODE_SSN_NO_MATCH);
    }
  }

  async runChildrenBusinessChecks() {
    this.checkSsnMismatch();
    this.checkCoappSsn();

    return this.inputChildren.ssn.runBusinessChecks().then(() => {
      this.inputChildren.email.runBusinessChecks();
      this.inputChildren.dob.runBusinessChecks();
      return Promise.resolve();
    });
  }

  checkCoappSsn() {
    // Ensure that primary and coapplicant SSNs are not the same. This check is only done on the step-communication-v2 page as the coapplicant fields can be edited without saving to the application.
    if (
      this.isCommunicationStepV2 &&
      this.isJointApplication &&
      this.isPrimary
    ) {
      this.pending = true;

      const coAppSsnField = this.getRootNode()
        .getElementById('coapp-form')
        ?.shadowRoot.getElementById('co-ssnInput');

      if (coAppSsnField) {
        const coAppVal = coAppSsnField.values.ssn;
        const primaryAppVal = this.inputChildren.ssn.values.ssn;

        const ssnMatch =
          !primaryAppVal || !coAppVal ? false : coAppVal === primaryAppVal;

        this.debug.log(`Ssn match ${ssnMatch}`);

        this.validity.compareSsns = !ssnMatch;

        if (ssnMatch) {
          this.inputChildren.ssn.setSsnMatch(true);
        } else {
          this.inputChildren.ssn.setSsnMatch(false);
          coAppSsnField.setSsnMatch(false);
        }
      }
      this.requestUpdate();
    }
    this.pending = false;
  }

  @validateForm()
  async onSubmit() {
    if (this.pending) return;

    // Check validity, update Application, then close editing card
    const cardComponent = this.shadowRoot.getElementById(
      `${this.coAppPrefix}personal-edit-card`
    );

    this.runChildrenBusinessChecks().then(() => {
      let valid = true;
      this.debug.log('Validity before business checks', this.validity);
      Object.values(this.validity).forEach((validityValue) => {
        if (!validityValue) {
          valid = false;
        }
      });

      this.debug.log(
        'Validity after business checks',
        this.validity,
        'valid',
        valid
      );

      if (valid) {
        this.oldApplicantInfo = structuredClone(
          ApplicationService.Application[this.applicantType]
        );
        this.saveInfoToApplicant();
        this.debug.log('valid', ApplicationService.Application);
        cardComponent?.closeEditing();
        this.displayError = false;
        this.dispatchEvent(
          new CustomEvent('edit-card-closed', {
            bubbles: true,
            composed: true,
            detail: {
              component: `${this.coAppPrefix}refi-personal-communication`,
            },
          })
        );
      }
    });
  }

  saveInfoToApplicant() {
    // Update to mobile if user changed from prefilled phone number
    const app = ApplicationService.Application;
    const initialPhoneNumber = app[this.applicantType].phoneNumber;
    this.oldApplicantInfo = structuredClone(app[this.applicantType]);

    // Collect all updates
    const applicantUpdates = {};
    Object.values(this.inputChildren).forEach((child) => {
      if (child.values !== null) {
        Object.keys(child.values).forEach((field) => {
          const fieldName =
            this.applicantType === this.coAppType &&
            field.substring(0, 3) === 'co-'
              ? field.substring(3)
              : field;
          applicantUpdates[fieldName] = child.values[field];
        });
      }
    });

    // Add phone type if number changed
    if (applicantUpdates.phoneNumber !== initialPhoneNumber) {
      applicantUpdates.phoneType = 'MOBILE';
    }

    // Update tags
    this.inputChildren.dob.updateDobChangedTag();
    this.inputChildren.ssn.updateSsnChangedTag();

    if (this.applicantType === this.primaryAppType) {
      ApplicationService.primaryApplicantEmailChanged = false;
    } else {
      ApplicationService.coApplicantEmailChanged = false;
    }

    // Update application once with all changes
    ApplicationService.Application = {
      ...ApplicationService.Application,
      [this.applicantType]: {
        ...ApplicationService.Application[this.applicantType],
        ...applicantUpdates,
      },
    };

    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('host-app-confirmation-page-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'update-application-personal',
          data: { application: ApplicationService.Application },
        },
      })
    );
    this.tagFieldChangesOnSubmit();
    this.resetValues();
  }

  tagFieldChangesOnSubmit() {
    Object.keys(ApplicationService.Application[this.applicantType]).forEach(
      (field) => {
        if (
          typeof ApplicationService.Application[this.applicantType][field] !==
            'object' &&
          ApplicationService.Application[this.applicantType][field] !==
            this.oldApplicantInfo[field]
        ) {
          this.debug.log(`${this.coAppPrefix}${field} changed`);
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `${this.coAppPrefix}${field}`,
            },
          });
        }
      }
    );
  }
}

customElements.define(
  'refi-personal-communication',
  RefiPersonalCommunicationComponent
);

// Defining it again so that we can import a separate instance for coapp on confirmation page
customElements.define(
  'co-refi-personal-communication',
  class extends RefiPersonalCommunicationComponent {}
);
